/**
 * Centralized color definitions for timeline visualizations
 *
 * These Tailwind fill classes are used for SVG rendering in:
 * - RunVisualisation component
 * - BatchList timeline component
 */

// =============================================================================
// ACTION COLORS
// =============================================================================

/**
 * Pre-defined color mappings for known action types.
 * Uses Tailwind fill classes for SVG compatibility.
 */
export const ACTION_COLORS: Record<string, string> = {
  peel: 'fill-blue-500',
  shake: 'fill-red-500',
  run: 'fill-lime-500',
  do_nothing: 'fill-slate-400',
  store: 'fill-pink-500',
  transport: 'fill-amber-400',
  manual: 'fill-purple-500',
  action: 'fill-emerald-500',
}

/**
 * Default color for unknown actions
 */
export const DEFAULT_ACTION_COLOR = 'fill-slate-500'

// =============================================================================
// INSTRUMENT COLORS
// =============================================================================

/**
 * Pre-defined color mappings for known instrument types.
 * Uses Tailwind fill classes for SVG compatibility.
 */
export const INSTRUMENT_COLORS: Record<string, string> = {
  'Xpeel Peel': 'fill-blue-500',
  Multidrop: 'fill-emerald-500',
  'Hamilton 1': 'fill-purple-500',
  transport: 'fill-slate-400',
}

/**
 * Default color for unknown instruments
 */
export const DEFAULT_INSTRUMENT_COLOR = 'fill-slate-500'

// =============================================================================
// BATCH COLORS
// =============================================================================

/**
 * Colors for batch indicators (cycling through for multiple batches)
 */
export const BATCH_COLORS = [
  'fill-red-500',
  'fill-orange-500',
  'fill-amber-500',
]

// =============================================================================
// FILL VARIANT MAPS
// =============================================================================

/** Fill → darker stroke for 1px borders */
export const FILL_TO_DARK_STROKE: Record<string, string> = {
  'fill-blue-500': 'stroke-blue-700',
  'fill-red-500': 'stroke-red-700',
  'fill-lime-500': 'stroke-lime-700',
  'fill-slate-400': 'stroke-slate-600',
  'fill-slate-500': 'stroke-slate-700',
  'fill-pink-500': 'stroke-pink-700',
  'fill-amber-400': 'stroke-amber-600',
  'fill-amber-500': 'stroke-amber-700',
  'fill-purple-500': 'stroke-purple-700',
  'fill-emerald-500': 'stroke-emerald-700',
  'fill-cyan-500': 'stroke-cyan-700',
  'fill-violet-500': 'stroke-violet-700',
  'fill-gray-500': 'stroke-gray-700',
  'fill-orange-500': 'stroke-orange-700',
}

/** Fill → lighter fill for hover state */
export const FILL_TO_LIGHT_FILL: Record<string, string> = {
  'fill-blue-500': 'fill-blue-300',
  'fill-red-500': 'fill-red-300',
  'fill-lime-500': 'fill-lime-300',
  'fill-slate-400': 'fill-slate-300',
  'fill-slate-500': 'fill-slate-400',
  'fill-pink-500': 'fill-pink-300',
  'fill-amber-400': 'fill-amber-300',
  'fill-amber-500': 'fill-amber-300',
  'fill-purple-500': 'fill-purple-300',
  'fill-emerald-500': 'fill-emerald-300',
  'fill-cyan-500': 'fill-cyan-300',
  'fill-violet-500': 'fill-violet-300',
  'fill-gray-500': 'fill-gray-300',
  'fill-orange-500': 'fill-orange-300',
}

/** Ordered palette for per-step coloring */
export const STEP_COLORS = [
  'fill-blue-500',
  'fill-emerald-500',
  'fill-purple-500',
  'fill-amber-500',
  'fill-pink-500',
  'fill-red-500',
  'fill-lime-500',
  'fill-slate-500',
]

/** Ordered palette for per-workflow coloring in joint (parallel) plans */
export const WORKFLOW_COLORS = STEP_COLORS

/** Fill for tasks shared across more than one workflow in a joint plan */
export const SHARED_WORKFLOW_COLOR = 'fill-gray-500'

/** Default fill for tasks with no workflow attribution */
export const DEFAULT_WORKFLOW_COLOR = 'fill-slate-400'

// =============================================================================
// DEVIATION COLORS
// =============================================================================

/**
 * Text colors for deviation display (positive = overrun, negative = early)
 */
export const DEVIATION_COLORS = {
  positive: 'text-destructive',
  negative: 'text-green-600',
}

/**
 * Returns the appropriate text color class for a deviation value.
 * Positive (overrun) → destructive, Negative (early) → green, Zero → empty
 */
export const getDeviationColor = (seconds: number): string => {
  if (seconds > 0) return DEVIATION_COLORS.positive
  if (seconds < 0) return DEVIATION_COLORS.negative
  return ''
}

// =============================================================================
// UI ELEMENT COLORS
// =============================================================================

/**
 * Color for progress indicator line
 */
export const PROGRESS_INDICATOR_COLOR = 'stroke-red-600'

/**
 * Colors for grid lines
 */
export const GRID_COLORS = {
  major: 'stroke-gray-200',
  minor: 'stroke-gray-100',
}
